// The Big Knock reveal's hard visitor (sprint 29 fix pass): the event's visitor behind the door's reeded glass, playing the door's own reveal for its kind
// in stepped 15 fps frames: the alien stretches in jumps and its eyes go red, or the neighbour turns and walks off into the snow. It is the door's code
// (visitors.js drawGlass, reactions.js glassState) drawn into a small canvas; the look is the one the knock showed (the round id "bk-<n>" is the seed).
// Art hook for the artist: .bk-art.visitor in client/bigknock.css holds one canvas; the frame is visitorFrame() below.
import { GLASS, makeSilhouette, seedOf, drawGlass } from './visitors.js';
import { glassState, revealLamp, FPS } from './reactions.js';
import { createBits } from './bits.js';

export const PAD = 8; // ink round the glass, in glass pixels
export const AT_GLASS = 0.5; // seconds at the glass before the reveal starts
export const STILL_T = 2.4; // reduced motion: the frame shown, this long after the reveal started (every reaction is well under way)
export const HOLD_S = 6; // the animation holds its last frame from here on
export const SIZE = { w: GLASS.w + 2 * PAD, h: GLASS.h + 2 * PAD };
export const SCALE = 2; // CSS px per glass pixel: the stage's own 2x (the one-bit stage is never drawn smaller), whole pixels, image-rendering pixelated
// the door's own reveal for each kind: an alien let in stretches, a neighbour kept out walks off (client/reactions.js revealPose)
export const reactionOf = kind => (kind === 'alien' ? { kind: 'alien', verdict: 'open' } : { kind: 'human', verdict: 'shut' });

// One frame, t seconds after the box opened: a pure function of n, kind and the 15 fps frame number. Returns the bits (ink, paper, red).
export function visitorFrame({ n, kind, t, B = createBits(SIZE.w, SIZE.h) }) {
  const fr = Math.floor(t * FPS + 1e-6), revealT = Math.floor((t - AT_GLASS) * FPS + 1e-6) / FPS, r = reactionOf(kind);
  const sil = makeSilhouette(seedOf(`bk-${n}`), { outbreak: false, halloween: false });
  B.fill(0, 0, SIZE.w, SIZE.h, 0);
  drawGlass(B, PAD, PAD, glassState({ sil, kind: r.kind, verdict: r.verdict, revealT, fr: revealT >= 0 ? Math.round(revealT * FPS) : fr, t: fr / FPS, lamp: revealLamp(r.kind, revealT, fr, fr / FPS), approach: 1 }));
  return B;
}

// Mounts the visitor into `box` and plays it; returns stop(). Reduced motion draws one frame, the last. doc/raf/perf are injected for tests.
export function mountVisitor(box, { n, kind, reduced = false, doc = document, raf = f => requestAnimationFrame(f), cancel = id => cancelAnimationFrame(id), perf = () => performance.now() }) {
  const canvas = doc.createElement('canvas');
  canvas.width = SIZE.w; canvas.height = SIZE.h;
  canvas.style.width = SIZE.w * SCALE + 'px'; canvas.style.height = SIZE.h * SCALE + 'px';
  const g = canvas.getContext('2d'), img = g.createImageData(SIZE.w, SIZE.h), d32 = new Uint32Array(img.data.buffer);
  box.append(canvas);
  const draw = t => { const B = visitorFrame({ n, kind, t }); B.blit(d32); g.putImageData(img, 0, 0); };
  if (reduced) { draw(AT_GLASS + STILL_T); return () => {}; }
  const t0 = perf();
  let last = -1, id = 0, stopped = false;
  const tick = now => {
    if (stopped) return;
    const t = Math.min(HOLD_S, (now - t0) / 1000), f = Math.floor(t * FPS);
    if (f !== last) { last = f; draw(t); } // stepped: redraw only on a new 15 fps frame
    if (t < HOLD_S) id = raf(tick);
  };
  tick(t0);
  return () => { stopped = true; cancel(id); };
}
